<template>
  <div class="page-container">
    <div class="content" v-html="policyContent"></div>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPage',
  data() {
    return {
      policyContent: `
        <h1>Privacy Policy for Mind Your Bite</h1>
        <p><strong>Last Updated:</strong> 7.7.2025</p>
        <hr>
        <h2>Introduction</h2>
        <p>Welcome to Mind Your Bite! We respect and take your privacy seriously. We are committed to protecting your personal data. This Privacy Policy describes how Mind Your Bite ("we," "our," or "us") collects, uses, and shares personal information when you use our mobile application and website (collectively, the "Service").</p>
        <p>By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy. We are committed to protecting your personal data and providing transparency about our data practices.</p>
        <hr>
        <h2>Information We Collect</h2>
        <h3>1. Information You Provide to Us</h3>
        <p><strong>Account Information:</strong></p>
        <ul>
          <li>Email address</li>
          <li>Name</li>
          <li>Username</li>
          <li>Password (encrypted)</li>
          <li>Profile information (age, dietary preferences, health goals)</li>
        </ul>
        <p><strong>Dietary and Health Information:</strong></p>
        <ul>
          <li>Food preferences and restrictions</li>
          <li>Dietary goals and objectives</li>
          <li>Health conditions affecting food choices</li>
          <li>Nutritional tracking data</li>
          <li>Product scanning history</li>
        </ul>
        <p><strong>User-Generated Content:</strong></p>
        <ul>
          <li>Product reviews and ratings</li>
          <li>Community posts and comments</li>
          <li>Photos of food products</li>
          <li>Custom food entries</li>
        </ul>
        <p><strong>Communication Data:</strong></p>
        <ul>
          <li>Messages sent through our support system</li>
          <li>Feedback and survey responses</li>
          <li>Newsletter subscription preferences</li>
        </ul>
        <h3>2. Information Automatically Collected</h3>
        <p><strong>Device Information:</strong></p>
        <ul>
          <li>Device type and model</li>
          <li>Operating system and version</li>
          <li>Unique device identifiers</li>
          <li>IP address</li>
          <li>Browser type and version</li>
        </ul>
        <p><strong>Usage Data:</strong></p>
        <ul>
          <li>App usage patterns and frequency</li>
          <li>Features accessed and time spent</li>
          <li>Product scanning activity</li>
          <li>Search queries</li>
          <li>Navigation patterns within the app</li>
        </ul>
        <p><strong>Location Information:</strong></p>
        <ul>
          <li>Approximate location (city/region level) for relevant product recommendations</li>
          <li>Precise location only when explicitly granted for store locator features</li>
        </ul>
        <h3>3. Information from Third-Party Sources</h3>
        <p><strong>Nutrition Databases:</strong></p>
        <ul>
          <li>Product information from Open Food Facts</li>
          <li>Nutritional data from Edamam API</li>
          <li>Barcode and product databases</li>
        </ul>
        <p><strong>Analytics Services:</strong></p>
        <ul>
          <li>Usage analytics from third-party providers</li>
          <li>Crash reporting and performance data</li>
        </ul>
        <hr>
        <h2>How We Use Your Information</h2>
        <h3>Primary Purposes</h3>
        <p><strong>Service Provision:</strong></p>
        <ul>
          <li>Provide personalized food recommendations</li>
          <li>Analyze product nutritional content</li>
          <li>Maintain your dietary preferences and goals</li>
          <li>Enable barcode scanning functionality</li>
        </ul>
        <p><strong>Personalization:</strong></p>
        <ul>
          <li>Customize app experience based on your preferences</li>
          <li>Provide relevant product alternatives</li>
          <li>Tailor content to your dietary needs and restrictions</li>
          <li>Improve recommendation algorithms</li>
        </ul>
        <p><strong>Communication:</strong></p>
        <ul>
          <li>Send important service updates</li>
          <li>Respond to your inquiries and support requests</li>
          <li>Deliver newsletters (with your consent)</li>
          <li>Notify you of new features or changes</li>
        </ul>
        <p><strong>Safety and Security:</strong></p>
        <ul>
          <li>Protect against fraud and abuse</li>
          <li>Ensure platform security</li>
          <li>Verify user identity</li>
          <li>Prevent unauthorized access</li>
        </ul>
        <p><strong>Legal Compliance:</strong></p>
        <ul>
          <li>Comply with applicable laws and regulations</li>
          <li>Respond to legal requests</li>
          <li>Protect our rights and interests</li>
        </ul>
        <h3>Secondary Purposes</h3>
        <p><strong>Analytics and Improvement:</strong></p>
        <ul>
          <li>Analyze usage patterns to improve our Service</li>
          <li>Conduct research on nutrition and food preferences</li>
          <li>Develop new features and functionality</li>
          <li>Optimize app performance</li>
        </ul>
        <p><strong>Marketing (with consent):</strong></p>
        <ul>
          <li>Send promotional communications</li>
          <li>Provide information about new features</li>
          <li>Share relevant health and nutrition content</li>
        </ul>
        <hr>
        <h2>Information Sharing and Disclosure</h2>
        <p><strong>We Do Not Sell Your Personal Information</strong><br>Mind Your Bite does not sell, rent, or trade your personal information to third parties for commercial purposes.</p>
        <h3>When We May Share Information</h3>
        <p><strong>Service Providers:</strong></p>
        <ul>
          <li>Cloud hosting and storage providers</li>
          <li>Analytics and crash reporting services</li>
          <li>Customer support platforms</li>
          <li>Payment processing services (for premium features)</li>
        </ul>
        <p><strong>Nutrition Data Partners:</strong></p>
        <ul>
          <li>Open Food Facts (for product information)</li>
          <li>Edamam (for nutritional analysis)</li>
          <li>Other nutrition databases as needed</li>
        </ul>
        <p><strong>Legal Requirements:</strong></p>
        <ul>
          <li>When required by law or legal process</li>
          <li>To protect our rights, property, or safety</li>
          <li>To protect the rights, property, or safety of our users</li>
          <li>In connection with a merger, acquisition, or sale of assets</li>
        </ul>
        <p><strong>With Your Consent:</strong></p>
        <ul>
          <li>When you explicitly agree to share information</li>
          <li>For specific features that require data sharing</li>
          <li>With community features (reviews, ratings)</li>
        </ul>
        <p><strong>Data Anonymization</strong><br>When sharing data for research or analytics purposes, we remove or encrypt personally identifiable information to protect your privacy.</p>
        <hr>
        <h2>Data Retention</h2>
        <ul>
          <li><strong>Account Data:</strong> Retained while your account is active and for a reasonable period after account deletion to comply with legal obligations.</li>
          <li><strong>Usage Data:</strong> Typically retained for 2-3 years for analytics and improvement purposes.</li>
          <li><strong>Health and Dietary Data:</strong> Retained while your account is active and deleted upon account deletion, unless required for legal compliance.</li>
          <li><strong>Communication Data:</strong> Retained for customer service purposes, typically 2-3 years.</li>
          <li><strong>Legal Hold:</strong> Data may be retained longer when required by law or for legal proceedings.</li>
        </ul>
        <hr>
        <h2>Your Rights and Choices</h2>
        <h3>Access and Control</h3>
        <p><strong>Account Management:</strong></p>
        <ul>
          <li>Access and update your profile information</li>
          <li>Modify dietary preferences and goals</li>
          <li>Review your scanning and usage history</li>
          <li>Download your data in a portable format</li>
        </ul>
        <p><strong>Privacy Controls:</strong></p>
        <ul>
          <li>Opt out of promotional communications</li>
          <li>Adjust location sharing preferences</li>
          <li>Control community feature participation</li>
          <li>Manage cookie preferences (website)</li>
        </ul>
        <p><strong>Data Deletion:</strong></p>
        <ul>
          <li>Request deletion of your account and associated data</li>
          <li>Remove specific data entries or content</li>
          <li>Withdraw consent for optional data processing</li>
        </ul>
        <h3>Regional Rights</h3>
        <p><strong>European Union (GDPR):</strong></p>
        <ul>
          <li>Right to access your personal data</li>
          <li>Right to rectification of inaccurate data</li>
          <li>Right to erasure ("right to be forgotten")</li>
          <li>Right to restrict processing</li>
          <li>Right to data portability</li>
          <li>Right to object to processing</li>
          <li>Right to withdraw consent</li>
        </ul>
        <p><strong>California (CCPA):</strong></p>
        <ul>
          <li>Right to know what personal information is collected</li>
          <li>Right to delete personal information</li>
          <li>Right to opt-out of sale (we don't sell data)</li>
          <li>Right to non-discrimination</li>
        </ul>
        <p><strong>Other Jurisdictions:</strong><br>We comply with applicable privacy laws in all jurisdictions where we operate.</p>
        <hr>
        <h2>Data Security</h2>
        <h3>Security Measures</h3>
        <p><strong>Technical Safeguards:</strong></p>
        <ul>
          <li>Encryption of data in transit and at rest</li>
          <li>Secure authentication systems</li>
          <li>Regular security audits and assessments</li>
          <li>Access controls and monitoring</li>
        </ul>
        <p><strong>Organizational Safeguards:</strong></p>
        <ul>
          <li>Employee training on data protection</li>
          <li>Limited access to personal data</li>
          <li>Regular review of data handling practices</li>
          <li>Incident response procedures</li>
        </ul>
        <p><strong>Third-Party Security:</strong></p>
        <ul>
          <li>Vendor security assessments</li>
          <li>Contractual data protection requirements</li>
          <li>Regular monitoring of service providers</li>
        </ul>
        <h3>Data Breach Response</h3>
        <p>In the event of a data breach, we will:</p>
        <ul>
          <li>Investigate and contain the breach promptly</li>
          <li>Notify affected users within 72 hours when required</li>
          <li>Report to relevant authorities as required by law</li>
          <li>Take steps to prevent future breaches</li>
        </ul>
        <hr>
        <h2>International Data Transfers</h2>
        <p>Our servers are hosted in multiple countries worldwide through Google Cloud and Firebase data centers, including the United States, Europe, and Asia. We may transfer your personal information to countries other than your country of residence for processing and storage. When we do so, we ensure appropriate safeguards are in place, including:</p>
        <ul>
          <li>Standard Contractual Clauses approved by relevant authorities</li>
          <li>Adequacy decisions by relevant data protection authorities</li>
          <li>Other legally recognized transfer mechanisms</li>
        </ul>
        <hr>
        <h2>Children's Privacy</h2>
        <p>Mind Your Bite is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information promptly.</p>
        <p>For users between 13-18 years of age, we recommend parental guidance when using our Service, particularly for health and dietary tracking features.</p>
        <hr>
        <h2>Cookies and Tracking Technologies</h2>
        <h3>Website Cookies</h3>
        <p>Our website uses cookies and similar technologies to:</p>
        <ul>
          <li>Remember your preferences and settings</li>
          <li>Analyze website usage and performance</li>
          <li>Provide personalized content</li>
          <li>Enable certain website functionality</li>
        </ul>
        <h3>Mobile App Analytics</h3>
        <p>Our mobile app uses analytics tools to:</p>
        <ul>
          <li>Track app usage and performance</li>
          <li>Identify and fix technical issues</li>
          <li>Understand user behavior patterns</li>
          <li>Improve app functionality</li>
        </ul>
        <h3>Your Choices</h3>
        <p>You can control cookies through your browser settings and opt out of certain analytics through app settings or device-level controls.</p>
        <hr>
        <h2>Third-Party Services</h2>
        <h3>Integrated Services</h3>
        <p><strong>Open Food Facts:</strong></p>
        <ul>
          <li>Provides product database information</li>
          <li>Subject to their privacy policy: <a href="https://world.openfoodfacts.org/privacy" target="_blank" rel="noopener noreferrer">https://world.openfoodfacts.org/privacy</a></li>
        </ul>
        <p><strong>Edamam:</strong></p>
        <ul>
          <li>Provides nutritional analysis services</li>
          <li>Subject to their privacy policy: <a href="https://www.edamam.com/privacy/" target="_blank" rel="noopener noreferrer">https://www.edamam.com/privacy/</a></li>
        </ul>
        <p><strong>Analytics Providers:</strong></p>
        <ul>
          <li>Google Analytics (with anonymization)</li>
          <li>Firebase Analytics</li>
          <li>Crashlytics for error reporting</li>
        </ul>
        <h3>Third-Party Links</h3>
        <p>Our Service may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any personal information.</p>
        <hr>
        <h2>Changes to This Privacy Policy</h2>
        <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make changes, we will:</p>
        <ul>
          <li>Update the "Last Updated" date at the top of this policy</li>
          <li>Notify you through the app or via email for material changes</li>
          <li>Provide a summary of key changes when significant</li>
          <li>Obtain your consent when required by law</li>
        </ul>
        <p>We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.</p>
        <hr>
        <h2>Contact Us</h2>
        <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
        <p><strong>Email:</strong> hello@getfoundlab.com</p>
        <p>For users in the European Union, you also have the right to lodge a complaint with your local data protection authority.</p>
        <hr>
        <h2>Legal Basis for Processing (EU Users)</h2>
        <p>We process your personal data based on the following legal grounds:</p>
        <ul>
          <li><strong>Contract:</strong> To provide our Service and fulfill our contractual obligations</li>
          <li><strong>Legitimate Interest:</strong> To improve our Service, ensure security, and conduct analytics</li>
          <li><strong>Consent:</strong> For marketing communications and optional features</li>
          <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
        </ul>
        <hr>
        <h2>Effective Date</h2>
        <p>This Privacy Policy is effective as of 7.7.2025 and applies to all information collected by Mind Your Bite from that date forward.</p>
      `
    }
  }
}
</script>

<style scoped>
.page-container {
  max-width: 800px;
  margin: 40px auto;
  padding: 0 20px;
  min-height: 100vh;
  background-color: white;
}
.content {
  line-height: 1.8;
  font-size: 16px;
  color: #333;
}
.content >>> h1 {
  font-size: 36px;
  margin-bottom: 20px;
}
.content >>> h2 {
  font-size: 28px;
  margin-top: 40px;
  margin-bottom: 20px;
  border-bottom: 2px solid #eaeaea;
  padding-bottom: 10px;
}
.content >>> h3 {
  font-size: 22px;
  margin-top: 30px;
  margin-bottom: 15px;
}
.content >>> hr {
  border: 0;
  border-top: 1px solid #eee;
  margin: 40px 0;
}
.content >>> ul {
  padding-left: 20px;
}
.content >>> a {
  color: #3b82f6;
}
</style>
